const React = require('react');
const T = require('prop-types');
const { useState, useEffect, useRef, useCallback, useMemo } = React;
const { context } = require('app-context');
const useForm = require('hooks/useForm');
const FormConfig = require('./formConfig');
const OfferTargeting = require('./OfferTargeting');
const TargetPayload = require('./targetPayload');
const PreventNavigationDialog = require('components/PreventNavigationDialog');
const SimpleDialog = require('components/SimpleDialog');
const { useTheme } = require('@mui/material/styles');
const { default: Box } = require('@mui/material/Box');
const { default: Typography } = require('@mui/material/Typography');
const { default: Button } = require('@mui/material/Button');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: CircularProgress } = require('@mui/material/CircularProgress');
const { default: LinearProgress } = require('@mui/material/LinearProgress');
const { default: Alert } = require('@mui/material/Alert');
const { default: Divider } = require('@mui/material/Divider');
const { default: Accordion } = require('@mui/material/Accordion');
const { default: AccordionSummary } = require('@mui/material/AccordionSummary');
const { default: AccordionDetails } = require('@mui/material/AccordionDetails');
const { default: ArrowBackIcon } = require('@mui/icons-material/ArrowBack');
const { default: ExpandMoreIcon } = require('@mui/icons-material/ExpandMore');
const { OFFER_CREATION_METHODS } = require('utils/constants');

const api = context.api.nearpeer;
const actions = context.actions;
const redux = context.redux.hooks;

module.exports = function AdminOfferFormPage(props) {

    const { history, match, location } = props;
    const offerId = match?.params?.id ? Number(match.params.id) : null;
    const isEdit = !!offerId;
    const isManualCreate = !isEdit && location?.pathname?.endsWith('/create/manual');
    const theme = useTheme();
    const defaultCtaBgColor = theme.palette.primary.main;

    const [defaults, setDefaults] = useState(() => FormConfig.withCtaColorDefaults(FormConfig.EMPTY_DEFAULTS, defaultCtaBgColor));
    const [targets, setTargets] = useState([]);
    const [initialTargets, setInitialTargets] = useState([]);
    const [targetingValid, setTargetingValid] = useState(true);
    const [targetsDirty, setTargetsDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [error, setError] = useState(null);
    const [confirmBroadTargeting, setConfirmBroadTargeting] = useState(null);
    const [confirmPushNotificationsOpen, setConfirmPushNotificationsOpen] = useState(false);
    const [processingDialogOpen, setProcessingDialogOpen] = useState(false);
    const [saveResult, setSaveResult] = useState(null);
    const [deliveryProgress, setDeliveryProgress] = useState(null);
    const [processingError, setProcessingError] = useState(null);
    const [statsOnly, setStatsOnly] = useState(false);

    const targetsRef = useRef(targets);
    const targetingValidRef = useRef(true);
    const targetsInitializedRef = useRef(false);
    const hasSavedRef = useRef(false);
    const savingRef = useRef(false);
    const pendingFormDataRef = useRef(null);

    useEffect(() => {

        targetingValidRef.current = targetingValid;
    }, [targetingValid]);

    useEffect(() => {

        targetsRef.current = targets;
    }, [targets]);

    useEffect(() => {

        if (!isEdit) {
            return;
        }

        let skipLoadingComplete = false;

        api.offers.adminList()
            .then(({ entities }) => {

                const offer = entities.offers?.[offerId];
                if (!offer) {
                    setError('Offer not found');
                    return;
                }

                const { targets: offerTargets, ...offerFields } = offer;

                if (FormConfig.isImageUploadOffer(offer)) {
                    skipLoadingComplete = true;
                    history.replace(FormConfig.getOfferEditPath(offer));
                    return;
                }

                const loaded = FormConfig.withCtaColorDefaults(FormConfig.EMPTY_DEFAULTS, defaultCtaBgColor);
                Object.keys(loaded).forEach((key) => {

                    if (offerFields[key] != null) {
                        loaded[key] = offerFields[key];
                    }
                });

                setDefaults(loaded);
                setInitialTargets(offerTargets || []);
            })
            .catch((err) => setError(err.message || 'Failed to load offer'))
            .finally(() => {

                if (!skipLoadingComplete) {
                    setLoading(false);
                }
            });
    }, [defaultCtaBgColor, history, isEdit, offerId]);

    const performSave = useCallback(async (formData) => {

        if (savingRef.current) {
            return;
        }

        savingRef.current = true;
        setSaving(true);
        setError(null);
        setProcessingError(null);
        setSaveResult(null);
        setDeliveryProgress(null);
        setStatsOnly(false);
        setProcessingDialogOpen(true);

        try {
            // The API treats targets as the complete desired set, so offer fields and
            // targeting are saved together in one request.
            const payload = TargetPayload.withTargets(
                FormConfig.buildOfferPayload(formData),
                targetsRef.current
            );
            let result;

            if (isEdit) {
                result = await api.offers.adminUpdate(offerId, payload);
            }
            else {
                payload.creationMethod = OFFER_CREATION_METHODS.MANUAL;
                result = await api.offers.adminCreate(payload);
            }

            setSaveResult(result);
            redux.dispatch(actions.alerts.notification.push({
                message: isEdit ? 'Changes Saved' : 'Offer Created'
            }));
            hasSavedRef.current = true;
        }
        catch (err) {
            const message = err.message || 'Save failed';

            setError(message);
            setProcessingError(message);
        }
        finally {
            savingRef.current = false;
            setSaving(false);
        }
    }, [isEdit, offerId]);

    const continueSave = useCallback((formData) => {

        const kind = OfferTargeting.getBroadTargetingKind(targetsRef.current);

        if (kind) {
            pendingFormDataRef.current = formData;
            setConfirmBroadTargeting(kind);
            return;
        }

        return performSave(formData);
    }, [performSave]);

    const handleSave = useCallback((formData) => {

        if (!targetingValidRef.current) {
            setError('Please complete all targeting rows (choose a school and at least one role), or select "Target all students across all schools" or "Target all prospects across all schools".');
            return;
        }

        if (FormConfig.shouldConfirmPushNotifications(defaults, formData)) {
            pendingFormDataRef.current = formData;
            setConfirmPushNotificationsOpen(true);
            return;
        }

        return continueSave(formData);
    }, [continueSave, defaults]);

    const handleConfirmPushNotifications = useCallback(() => {

        setConfirmPushNotificationsOpen(false);
        const formData = pendingFormDataRef.current;
        pendingFormDataRef.current = null;
        if (formData) {
            continueSave(formData);
        }
    }, [continueSave]);

    const handleCancelPushNotifications = useCallback(() => {

        setConfirmPushNotificationsOpen(false);
        pendingFormDataRef.current = null;
    }, []);

    const handleConfirmBroadTargeting = useCallback(() => {

        setConfirmBroadTargeting(null);
        const formData = pendingFormDataRef.current;
        pendingFormDataRef.current = null;
        if (formData) {
            performSave(formData);
        }
    }, [performSave]);

    const handleCancelBroadTargeting = useCallback(() => {

        setConfirmBroadTargeting(null);
        pendingFormDataRef.current = null;
    }, []);

    const handleCheckNotificationStats = useCallback(async () => {

        if (!offerId) {
            return;
        }

        setStatsOnly(true);
        setProcessingError(null);
        setSaveResult({
            id: offerId,
            status: 'approved',
            sendPushNotifications: true
        });
        setDeliveryProgress(null);
        setProcessingDialogOpen(true);

        try {
            const progress = await api.offers.adminNotificationProgress(offerId);
            setDeliveryProgress(progress);
        }
        catch (err) {
            setProcessingError(err.message || 'Unable to load notification stats');
        }
    }, [offerId]);

    const shouldPollDelivery = Boolean(
        saveResult?.id &&
        saveResult.status === 'approved' &&
        saveResult.sendPushNotifications
    );

    useEffect(() => {

        if (!processingDialogOpen || !shouldPollDelivery) {
            return undefined;
        }

        let stopped = false;
        let timeout;

        const poll = async () => {

            try {
                const progress = await api.offers.adminNotificationProgress(saveResult.id);

                if (stopped) {
                    return;
                }

                setDeliveryProgress(progress);

                if (!progress.isComplete) {
                    timeout = setTimeout(poll, 1000);
                }
            }
            catch (err) {
                if (!stopped) {
                    setProcessingError(err.message || 'Unable to check notification delivery progress');
                }
            }
        };

        poll();

        return () => {

            stopped = true;
            clearTimeout(timeout);
        };
    }, [processingDialogOpen, saveResult?.id, shouldPollDelivery]);

    const saveFinished = Boolean(saveResult);
    const deliveryFinished = !shouldPollDelivery || Boolean(deliveryProgress?.isComplete);
    const processingFinished = !saving && (
        Boolean(processingError) ||
        (saveFinished && deliveryFinished)
    );

    const handleProcessingDialogClose = useCallback(() => {

        if (!processingFinished) {
            return;
        }

        setProcessingDialogOpen(false);
    }, [processingFinished]);

    const handleTargetsChange = useCallback((newTargets) => {

        setTargets(newTargets);
        if (targetsInitializedRef.current) {
            setTargetsDirty(true);
        }
        else {
            targetsInitializedRef.current = true;
        }
    }, []);

    const resolvedFormConfig = useMemo(() => FormConfig.buildFormConfig({
        defaults,
        onSubmit: handleSave
    }), [defaults, handleSave]);

    const { Form, methods } = useForm(resolvedFormConfig);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const isFormDirty = !!(methods?.formState?.isDirty || targetsDirty);

    return (
        <Box sx={{ p: 2, maxWidth: 720, mx: 'auto', pb: 6 }}>
            <PreventNavigationDialog
                when={isFormDirty}
                navigate={(path, state) => history.push(path, state)}
                shouldBlockNavigation={() => !hasSavedRef.current && isFormDirty}
                alertMsg='You have unsaved changes. Do you want to save or discard them?'
                saveFunction={() => {
                    const formEl = document.getElementById(FormConfig.FORM_ID);
                    if (formEl) formEl.requestSubmit();
                }}
                pageHasErrors={true}
            />
            <SimpleDialog
                isOpen={processingDialogOpen}
                onRequestClose={() => undefined}
                disableEscapeKeyDown
                fullWidth
                maxWidth='md'
                title={processingError ? 'Notification stats error' : (statsOnly ? 'Notification stats' : (processingFinished ? 'Offer processing complete' : (saving ? 'Preparing offer…' : 'Notification delivery progress')))}
                content={(
                    <Box sx={{ maxHeight: '68vh', overflowY: 'auto', pr: 0.5 }}>
                        {!processingError && !processingFinished && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <CircularProgress size={22} />
                                <Typography>
                                    {saving ? 'Saving the offer and finding matching users…' : 'Processing notification delivery…'}
                                </Typography>
                            </Box>
                        )}

                        {saveResult?.notificationChanges && (
                            <Box sx={{ mb: shouldPollDelivery ? 2 : 0 }}>
                                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Matching users: {Number(
                                        (saveResult.notificationChanges.added || 0) +
                                        (saveResult.notificationChanges.retained || 0)
                                    ).toLocaleString()}
                                </Typography>
                                <Typography>New notifications: {Number(saveResult.notificationChanges.added || 0).toLocaleString()}</Typography>
                                <Typography>Existing notifications updated: {Number(saveResult.notificationChanges.retained || 0).toLocaleString()}</Typography>
                                <Typography>Notifications removed: {Number(saveResult.notificationChanges.removed || 0).toLocaleString()}</Typography>
                            </Box>
                        )}

                        {shouldPollDelivery && deliveryProgress && (
                            <Box>
                                <LinearProgress
                                    variant='determinate'
                                    value={deliveryProgress.total
                                        ? Math.min(100, (deliveryProgress.processed / deliveryProgress.total) * 100)
                                        : 100}
                                    sx={{ mb: 1.5 }}
                                />
                                <Typography>Total active notifications: {Number(deliveryProgress.total || 0).toLocaleString()}</Typography>
                                <Typography>Transport processed: {Number(deliveryProgress.processed || 0).toLocaleString()}</Typography>
                                <Typography>Push delivered (active offer notifications): {Number(deliveryProgress.pushDelivered || 0).toLocaleString()}</Typography>
                                <Typography sx={{ pl: 2 }}>iOS: {Number(deliveryProgress.pushDeliveredIos || 0).toLocaleString()}</Typography>
                                <Typography sx={{ pl: 2 }}>Android: {Number(deliveryProgress.pushDeliveredAndroid || 0).toLocaleString()}</Typography>
                                <Typography>Read in bell menu: {Number(deliveryProgress.read || 0).toLocaleString()}</Typography>
                                <Typography>Skipped: {Number(deliveryProgress.skipped || 0).toLocaleString()}</Typography>
                                <Typography>Failed: {Number(deliveryProgress.failed || 0).toLocaleString()}</Typography>
                                <Typography>Still queued / processing: {Number(deliveryProgress.pending || 0).toLocaleString()}</Typography>

                                {!!deliveryProgress.groups?.length && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                                            By school / role ({deliveryProgress.groups.length})
                                        </Typography>
                                        {deliveryProgress.groups.map((group) => (
                                            <Accordion key={group.key} disableGutters>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                    <Box sx={{ width: '100%', pr: 1 }}>
                                                        <Typography sx={{ fontWeight: 600 }}>
                                                            {group.schoolName || `School ${group.schoolId || 'Unknown'}`} — {group.roleName || `Role ${group.roleId || 'Unknown'}`}
                                                        </Typography>
                                                        <Typography variant='caption' color='text.secondary'>
                                                            {Number(group.processed || 0).toLocaleString()} / {Number(group.total || 0).toLocaleString()} processed
                                                            {' · '}{Number(group.pushDelivered || 0).toLocaleString()} push delivered
                                                            {group.pending ? ` · ${Number(group.pending).toLocaleString()} pending` : ''}
                                                        </Typography>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Box
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
                                                            gap: 1
                                                        }}
                                                    >
                                                        <Typography variant='body2'>Total: {Number(group.total || 0).toLocaleString()}</Typography>
                                                        <Typography variant='body2'>Processed: {Number(group.processed || 0).toLocaleString()}</Typography>
                                                        <Typography variant='body2'>Pending: {Number(group.pending || 0).toLocaleString()}</Typography>
                                                        <Typography variant='body2'>Push: {Number(group.pushDelivered || 0).toLocaleString()}</Typography>
                                                        <Typography variant='body2'>iOS: {Number(group.pushDeliveredIos || 0).toLocaleString()}</Typography>
                                                        <Typography variant='body2'>Android: {Number(group.pushDeliveredAndroid || 0).toLocaleString()}</Typography>
                                                        <Typography variant='body2'>Read: {Number(group.read || 0).toLocaleString()}</Typography>
                                                        <Typography variant='body2'>Skipped: {Number(group.skipped || 0).toLocaleString()}</Typography>
                                                        <Typography variant='body2'>Failed: {Number(group.failed || 0).toLocaleString()}</Typography>
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {saveFinished && !shouldPollDelivery && !processingError && (
                            <Typography color='text.secondary'>
                                No push transport processing is required for this offer.
                            </Typography>
                        )}

                        {processingError && (
                            <Alert severity='error'>{processingError}</Alert>
                        )}
                    </Box>
                )}
                buttons={processingFinished ? (
                    <Button onClick={handleProcessingDialogClose} color='primary' variant='contained' autoFocus>
                        Done
                    </Button>
                ) : (
                    <Box sx={{ minWidth: 72 }} />
                )}
            />
            <SimpleDialog
                isOpen={confirmPushNotificationsOpen}
                onRequestClose={handleCancelPushNotifications}
                title='Send push notifications?'
                content='Are you sure you want to send push notifications to all matching users?'
                buttons={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleCancelPushNotifications} color='inherit'>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmPushNotifications} color='primary' variant='contained' autoFocus>
                            Yes, send push notifications
                        </Button>
                    </Box>
                }
            />
            <SimpleDialog
                isOpen={!!confirmBroadTargeting}
                onRequestClose={handleCancelBroadTargeting}
                title={OfferTargeting.BROAD_TARGETING_CONFIRM[confirmBroadTargeting]?.title}
                content={OfferTargeting.BROAD_TARGETING_CONFIRM[confirmBroadTargeting]?.content}
                buttons={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleCancelBroadTargeting} color='inherit'>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmBroadTargeting} color='primary' variant='contained' autoFocus>
                            Yes, target everyone
                        </Button>
                    </Box>
                }
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton
                    onClick={() => {

                        if (isEdit) {
                            history.push('/app/admin/offers');
                        }
                        else if (isManualCreate) {
                            history.push('/app/admin/offers/create');
                        }
                        else {
                            history.push('/app/admin/offers');
                        }
                    }}
                    aria-label='Back'
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant='h5' sx={{ fontWeight: 700, ml: 1 }}>
                    {isEdit ? 'Edit Offer' : (isManualCreate ? 'Manual Offer Creation' : 'New Offer')}
                </Typography>
            </Box>

            {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}

            {Form}

            <Divider sx={{ my: 2 }} />

            <OfferTargeting initialTargets={initialTargets} onChange={handleTargetsChange} onValidityChange={setTargetingValid} />

            {isEdit && (
                <Button
                    variant='outlined'
                    fullWidth
                    size='large'
                    onClick={handleCheckNotificationStats}
                    disabled={saving}
                    sx={{ mt: 2, borderRadius: 3 }}
                >
                    Check notification stats
                </Button>
            )}

            <Button
                variant='contained'
                fullWidth
                size='large'
                type='submit'
                form={FormConfig.FORM_ID}
                disabled={saving || !targetingValid}
                sx={{ mt: 2, borderRadius: 3 }}
            >
                {saving ? <CircularProgress size={22} /> : (isEdit ? 'Save Changes' : 'Create Offer')}
            </Button>
        </Box>
    );
};

module.exports.propTypes = {
    history: T.object,
    match: T.object,
    location: T.object
};
